import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, RefreshCw, Clock, DollarSign, Users, Settings, Bell, Shield, 
  Calendar, Timer, FileText, BarChart3, AlertCircle, CheckCircle,
  Globe, Zap, Shield as SecurityIcon, Eye, Palette
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const TimeTrackingSettings = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [settings, setSettings] = useState({
    // General Settings
    defaultWorkHours: 8,
    workDaysPerWeek: 5,
    timeZone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    
    // Time Tracking Settings
    autoStartTimer: false,
    requireDescription: true,
    allowOvertime: true,
    maxDailyHours: 12,
    minTimeEntry: 0.25,
    roundTimeEntries: true,
    roundToNearest: 0.25,
    
    // Billing Settings
    defaultHourlyRate: 75,
    currency: 'USD',
    taxRate: 0.08,
    billingIncrement: 0.25,
    autoCalculateAmount: true,
    
    // Approval Settings
    requireApproval: true,
    autoApprove: false,
    approvalDeadline: 7,
    notifyOnSubmission: true,
    notifyOnApproval: true,
    notifyOnRejection: true,
    
    // Notification Settings
    emailNotifications: true,
    browserNotifications: true,
    weeklyReports: true,
    monthlyReports: true,
    reminderFrequency: 'daily',
    
    // Integration Settings
    calendarIntegration: false,
    projectSync: true,
    taskSync: true,
    exportFormat: 'csv',
    
    // Security Settings
    ipRestriction: false,
    allowedIPs: [],
    sessionTimeout: 480,
    requireReauth: false,
    
    // Display Settings
    theme: 'light',
    compactView: false,
    showBillableOnly: false,
    groupByProject: true
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Simulate API call
      // In real implementation, load from API
      success('Settings loaded successfully!');
    } catch (err) {
      console.error('Error loading settings:', err);
      showError(err.message || 'Failed to load settings.');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      console.log('Saving settings:', settings);
      success('Settings saved successfully!');
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving settings:', err);
      showError(err.message || 'Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      loadSettings();
      setHasChanges(false);
    }
  };

  // Tab configuration
  const settingsTabs = [
    {
      id: 'general',
      label: 'General',
      icon: <Settings size={18} />,
      description: 'Basic settings and preferences'
    },
    {
      id: 'tracking',
      label: 'Time Tracking',
      icon: <Clock size={18} />,
      description: 'Time tracking behavior'
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: <DollarSign size={18} />,
      description: 'Billing rates and calculations'
    },
    {
      id: 'approval',
      label: 'Approval',
      icon: <CheckCircle size={18} />,
      description: 'Approval workflow settings'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <Bell size={18} />,
      description: 'Notification preferences'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <Zap size={18} />,
      description: 'External service connections'
    },
    {
      id: 'security',
      label: 'Security',
      icon: <SecurityIcon size={18} />,
      description: 'Security and access controls'
    },
    {
      id: 'display',
      label: 'Display',
      icon: <Eye size={18} />,
      description: 'Interface preferences'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Time Tracking Settings"
        description="Configure time tracking preferences and system behavior"
      />
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
          <button 
            className="btn-outline-cam" 
            onClick={handleReset}
            disabled={isSaving}
          >
            <RefreshCw size={16} />
            Reset
          </button>
          <button 
            className="btn-cam" 
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="me-2" />
                Save Settings
              </>
            )}
          </button>
        </div>

      {/* Settings Form with Tabs */}
      <div className="project-create-container">
        <form>
          {/* Settings Tabs */}
          <div className="settings-tabs-container">
            <div className="settings-tabs">
              {settingsTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="tab-icon">{tab.icon}</div>
                  <div className="tab-content">
                    <div className="tab-label">{tab.label}</div>
                    <div className="tab-description">{tab.description}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="settings-tab-content">
              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">General Settings</h3>
                      <p className="section-description">Basic time tracking configuration</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Default Work Hours per Day</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.defaultWorkHours}
                          onChange={(e) => handleInputChange('defaultWorkHours', parseInt(e.target.value))}
                          min="1"
                          max="24"
                          step="0.5"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Work Days per Week</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.workDaysPerWeek}
                          onChange={(e) => handleInputChange('workDaysPerWeek', parseInt(e.target.value))}
                          min="1"
                          max="7"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Time Zone</label>
                        <select
                          className="form-select"
                          value={settings.timeZone}
                          onChange={(e) => handleInputChange('timeZone', e.target.value)}
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                          <option value="Europe/London">London</option>
                          <option value="Europe/Paris">Paris</option>
                          <option value="Asia/Tokyo">Tokyo</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Date Format</label>
                        <select
                          className="form-select"
                          value={settings.dateFormat}
                          onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                        >
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Time Format</label>
                        <select
                          className="form-select"
                          value={settings.timeFormat}
                          onChange={(e) => handleInputChange('timeFormat', e.target.value)}
                        >
                          <option value="12h">12 Hour (AM/PM)</option>
                          <option value="24h">24 Hour</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Maximum Daily Hours</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.maxDailyHours}
                          onChange={(e) => handleInputChange('maxDailyHours', parseInt(e.target.value))}
                          min="1"
                          max="24"
                          step="0.5"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Minimum Time Entry (hours)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.minTimeEntry}
                          onChange={(e) => handleInputChange('minTimeEntry', parseFloat(e.target.value))}
                          min="0.1"
                          max="1"
                          step="0.05"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Round Time Entries</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.roundTimeEntries}
                            onChange={(e) => handleInputChange('roundTimeEntries', e.target.checked)}
                          />
                          <label className="form-check-label">Enable time rounding</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Time Tracking Tab */}
              {activeTab === 'tracking' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Time Tracking Behavior</h3>
                      <p className="section-description">Configure how time tracking works</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Auto-start Timer</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.autoStartTimer}
                            onChange={(e) => handleInputChange('autoStartTimer', e.target.checked)}
                          />
                          <label className="form-check-label">Start timer automatically</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Require Description</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.requireDescription}
                            onChange={(e) => handleInputChange('requireDescription', e.target.checked)}
                          />
                          <label className="form-check-label">Make description mandatory</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Allow Overtime</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.allowOvertime}
                            onChange={(e) => handleInputChange('allowOvertime', e.target.checked)}
                          />
                          <label className="form-check-label">Allow overtime entries</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Round to Nearest</label>
                        <select
                          className="form-select"
                          value={settings.roundToNearest}
                          onChange={(e) => handleInputChange('roundToNearest', parseFloat(e.target.value))}
                          disabled={!settings.roundTimeEntries}
                        >
                          <option value={0.1}>0.1 hours (6 minutes)</option>
                          <option value={0.25}>0.25 hours (15 minutes)</option>
                          <option value={0.5}>0.5 hours (30 minutes)</option>
                          <option value={1}>1 hour</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Billing Configuration</h3>
                      <p className="section-description">Set up billing rates and calculations</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Default Hourly Rate ($)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.defaultHourlyRate}
                          onChange={(e) => handleInputChange('defaultHourlyRate', parseFloat(e.target.value))}
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Currency</label>
                        <select
                          className="form-select"
                          value={settings.currency}
                          onChange={(e) => handleInputChange('currency', e.target.value)}
                        >
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="CAD">CAD (C$)</option>
                          <option value="AUD">AUD (A$)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Tax Rate (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.taxRate * 100}
                          onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) / 100)}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Billing Increment</label>
                        <select
                          className="form-select"
                          value={settings.billingIncrement}
                          onChange={(e) => handleInputChange('billingIncrement', parseFloat(e.target.value))}
                        >
                          <option value={0.1}>0.1 hours (6 minutes)</option>
                          <option value={0.25}>0.25 hours (15 minutes)</option>
                          <option value={0.5}>0.5 hours (30 minutes)</option>
                          <option value={1}>1 hour</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Auto-calculate Amount</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.autoCalculateAmount}
                            onChange={(e) => handleInputChange('autoCalculateAmount', e.target.checked)}
                          />
                          <label className="form-check-label">Calculate amounts automatically</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Approval Tab */}
              {activeTab === 'approval' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Approval Workflow</h3>
                      <p className="section-description">Configure timesheet approval process</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Require Approval</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.requireApproval}
                            onChange={(e) => handleInputChange('requireApproval', e.target.checked)}
                          />
                          <label className="form-check-label">Timesheets require approval</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Auto-approve</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.autoApprove}
                            onChange={(e) => handleInputChange('autoApprove', e.target.checked)}
                            disabled={!settings.requireApproval}
                          />
                          <label className="form-check-label">Auto-approve after deadline</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Approval Deadline (days)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.approvalDeadline}
                          onChange={(e) => handleInputChange('approvalDeadline', parseInt(e.target.value))}
                          min="1"
                          max="30"
                          disabled={!settings.requireApproval}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Session Timeout (minutes)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={settings.sessionTimeout}
                          onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                          min="30"
                          max="1440"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Notifications</h3>
                      <p className="section-description">Configure notification preferences</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Email Notifications</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.emailNotifications}
                            onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                          />
                          <label className="form-check-label">Send email notifications</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Browser Notifications</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.browserNotifications}
                            onChange={(e) => handleInputChange('browserNotifications', e.target.checked)}
                          />
                          <label className="form-check-label">Show browser notifications</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Weekly Reports</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.weeklyReports}
                            onChange={(e) => handleInputChange('weeklyReports', e.target.checked)}
                          />
                          <label className="form-check-label">Send weekly reports</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Monthly Reports</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.monthlyReports}
                            onChange={(e) => handleInputChange('monthlyReports', e.target.checked)}
                          />
                          <label className="form-check-label">Send monthly reports</label>
                        </div>
                      </div>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Notify on Submission</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.notifyOnSubmission}
                            onChange={(e) => handleInputChange('notifyOnSubmission', e.target.checked)}
                          />
                          <label className="form-check-label">Notify when timesheet submitted</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Notify on Approval</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.notifyOnApproval}
                            onChange={(e) => handleInputChange('notifyOnApproval', e.target.checked)}
                          />
                          <label className="form-check-label">Notify when timesheet approved</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Notify on Rejection</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.notifyOnRejection}
                            onChange={(e) => handleInputChange('notifyOnRejection', e.target.checked)}
                          />
                          <label className="form-check-label">Notify when timesheet rejected</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Reminder Frequency</label>
                        <select
                          className="form-select"
                          value={settings.reminderFrequency}
                          onChange={(e) => handleInputChange('reminderFrequency', e.target.value)}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="never">Never</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Integrations Tab */}
              {activeTab === 'integrations' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Integrations</h3>
                      <p className="section-description">Connect with external services</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Calendar Integration</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.calendarIntegration}
                            onChange={(e) => handleInputChange('calendarIntegration', e.target.checked)}
                          />
                          <label className="form-check-label">Sync with calendar</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Project Sync</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.projectSync}
                            onChange={(e) => handleInputChange('projectSync', e.target.checked)}
                          />
                          <label className="form-check-label">Sync with project management</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Task Sync</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.taskSync}
                            onChange={(e) => handleInputChange('taskSync', e.target.checked)}
                          />
                          <label className="form-check-label">Sync with task management</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Export Format</label>
                        <select
                          className="form-select"
                          value={settings.exportFormat}
                          onChange={(e) => handleInputChange('exportFormat', e.target.value)}
                        >
                          <option value="csv">CSV</option>
                          <option value="xlsx">Excel (XLSX)</option>
                          <option value="pdf">PDF</option>
                          <option value="json">JSON</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Security</h3>
                      <p className="section-description">Configure security and access controls</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">IP Restriction</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.ipRestriction}
                            onChange={(e) => handleInputChange('ipRestriction', e.target.checked)}
                          />
                          <label className="form-check-label">Restrict access by IP address</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Require Re-authentication</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.requireReauth}
                            onChange={(e) => handleInputChange('requireReauth', e.target.checked)}
                          />
                          <label className="form-check-label">Require re-auth for sensitive actions</label>
                        </div>
                      </div>
                    </div>

                    {settings.ipRestriction && (
                      <div className="form-group">
                        <label className="form-label">Allowed IP Addresses</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={settings.allowedIPs.join('\n')}
                          onChange={(e) => handleInputChange('allowedIPs', e.target.value.split('\n').filter(ip => ip.trim()))}
                          placeholder="Enter one IP address per line&#10;192.168.1.1&#10;10.0.0.1"
                        />
                        <small className="form-text text-muted">
                          Enter one IP address per line. Leave empty to allow all IPs.
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Display Tab */}
              {activeTab === 'display' && (
                <div className="settings-tab-panel active">
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Display Preferences</h3>
                      <p className="section-description">Customize the user interface</p>
                    </div>

                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Theme</label>
                        <select
                          className="form-select"
                          value={settings.theme}
                          onChange={(e) => handleInputChange('theme', e.target.value)}
                        >
                          <option value="light">Light</option>
                          <option value="dark">Dark</option>
                          <option value="auto">Auto (System)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Compact View</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.compactView}
                            onChange={(e) => handleInputChange('compactView', e.target.checked)}
                          />
                          <label className="form-check-label">Use compact interface</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Show Billable Only</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.showBillableOnly}
                            onChange={(e) => handleInputChange('showBillableOnly', e.target.checked)}
                          />
                          <label className="form-check-label">Hide non-billable entries</label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Group by Project</label>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={settings.groupByProject}
                            onChange={(e) => handleInputChange('groupByProject', e.target.checked)}
                          />
                          <label className="form-check-label">Group entries by project</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn-outline-cam"
              onClick={() => navigate('/projects/time-tracking')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-cam"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Saving Settings...
                </>
              ) : (
                <>
                  <Save size={18} className="me-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default TimeTrackingSettings;